import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import HeadLinks from '../components/headLinks';
import useCurrentLanguage from '../i18n/useCurrentLanguage';
import { relativeBaseUrl, buildUrlEnding } from '../i18n/environments';

type SeoProps = {
  description?: string;
  title?: string;
  image?: string;
  pageName?: string;
  keyPhrase?: string;
  children?: any;
  servicesPageSchema?: any;
  apiPageSchema?: any;
  price?: any;
};

const Seo: React.FC<SeoProps> = ({
  description,
  title,
  image,
  pageName,
  keyPhrase,
  children,
  servicesPageSchema,
  apiPageSchema,
  price,
}) => {
  const extractPlainText = (input) => {
    const getText = (item) =>
      typeof item === 'string'
        ? item
        : React.isValidElement(item)
        ? React.Children.toArray(item.props.children)
            .filter((child) => !(React.isValidElement(child) && child.props.className === 'tooltip'))
            .map(getText)
            .join('')
        : '';

    return Array.isArray(input) ? input.map(getText).join('') : getText(input);
  };

  const siteMetadata = {
    title: `Email Validation Tools & Email List Cleaning | ZeroBounce`,
    description: `Our Email Validation Service quickly and accurately removes invalid, disposable, spam traps, abusive, and other harmful email addresses. 99% Accuracy Guaranteed. Try out Free Trial now!`,
    author: `@zerobounce`,
  };

  title = extractPlainText(title);
  description = extractPlainText(description);

  const metaDescription = description ?? siteMetadata.description;
  const metaKeyPhrase = keyPhrase ? extractPlainText(keyPhrase) : metaDescription;

  const headLinks = HeadLinks();
  const { isEs } = useCurrentLanguage();

  return (
    <React.Fragment>
      <Helmet
        htmlAttributes={{
          lang: isEs ? 'es' : 'en',
        }}
        title={title}
        meta={[
          {
            name: `description`,
            content: metaDescription,
          },
          {
            name: `keywords`,
            content: metaKeyPhrase,
          },
          { name: 'mobile-web-app-capable', content: 'yes' },
          { name: 'Site-Version', content: 'v-' + process.env.REACT_APP_WEBSITE_APP_VERSION },
          {
            property: `og:title`,
            content: title ?? siteMetadata.title,
          },
          {
            property: `og:description`,
            content: metaDescription,
          },
          {
            property: `og:type`,
            content: `website`,
          },
          {
            property: `og:url`,
            content: pageName ? `${relativeBaseUrl}/` + pageName : `${relativeBaseUrl}/`,
          },
          {
            property: `og:image`,
            content: image ? `${relativeBaseUrl}/` + image : `${relativeBaseUrl}/zerobounce-generic-thumbnail.jpg`,
          },
          {
            name: `twitter:card`,
            content: `summary`,
          },
          {
            name: `twitter:site`,
            content: `${relativeBaseUrl}/`,
          },
          {
            name: `twitter:image`,
            content: image ? `${relativeBaseUrl}/` + image : `${relativeBaseUrl}/android-chrome-256x256.png`,
          },
          {
            name: `twitter:creator`,
            content: siteMetadata?.author ?? ``,
          },
          {
            name: `twitter:title`,
            content: title,
          },
          {
            name: `twitter:description`,
            content: metaDescription,
          },
          { name: 'msvalidate.01', content: '3744203857E165A089608FE1AE933DFC' },
        ]}
      >
        {children ? children : ''}
        {
          <script type="application/ld+json" async>
            {`
              {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": "${
        isEs
          ? pageName
            ? relativeBaseUrl + '/es/' + pageName
            : relativeBaseUrl + '/es/'
          : pageName
          ? relativeBaseUrl + '/' + pageName
          : relativeBaseUrl + '/'
      }",
      "name": "${title}",
      "description": "${description}",
      "url": "${
        isEs
          ? pageName
            ? relativeBaseUrl + '/es/' + pageName
            : relativeBaseUrl + '/es/'
          : pageName
          ? relativeBaseUrl + '/' + pageName
          : relativeBaseUrl + '/'
      }",
      "author": {
        "@type": "Organization",
        "name": "ZeroBounce",
        "url": "${relativeBaseUrl}",
        "logo": "https://www.zerobounce.net/logo.webp",
        "sameAs": [
          "https://www.facebook.com/zerobounce/",
          "https://www.linkedin.com/company/zerobounce-net",
          "https://www.instagram.com/zerobounce/",
          "https://x.com/zerobounce1"
        ],
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "US",
          "addressLocality": "Santa Barbara",
          "addressRegion": "California",
          "postalCode": "93101",
          "streetAddress": "10 E Yanonali St"
        }
      }
    },
    {
      "@type": "SoftwareApplication",
      "applicationCategory": "BusinessApplication",
      "name": "${title}",
      "description": "${description}",
      "url": "${
        isEs
          ? pageName
            ? relativeBaseUrl + '/es/' + pageName
            : relativeBaseUrl + '/es/'
          : pageName
          ? relativeBaseUrl + '/' + pageName
          : relativeBaseUrl + '/'
      }",
      "image": "https://www.zerobounce.net/logo.webp",
      "operatingSystem": "Web-based",
      "applicationSuite": "ZeroBounce Suite",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.81",
        "ratingCount": "2565"
      },
      "offers": {
        "@type": "Offer",
        "price": "${price ? price : 'Free'}",  
        "priceCurrency": "USD",
        "url": "${
          isEs
            ? pageName
              ? relativeBaseUrl + '/es/' + pageName
              : relativeBaseUrl + '/es/'
            : pageName
            ? relativeBaseUrl + '/' + pageName
            : relativeBaseUrl + '/'
        }"
      }
    }
  ]
}
          `}
          </script>
        }
        {servicesPageSchema && (
          <script data-react-helmet="true" type="application/ld+json" async>{`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "${isEs ? 'Inicio' : 'Home'}",
                "item": "${isEs ? `https://www.zerobounce.net/es/` : `https://www.zerobounce${buildUrlEnding}`}"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "${isEs ? 'Servicios' : 'Services'}",
                "item": "${isEs ? `https://www.zerobounce.net/es/services/` : `https://www.zerobounce${buildUrlEnding}/services/`}"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "${servicesPageSchema.serviceName}",
                "item": "${
                  isEs
                    ? `https://www.zerobounce.net/es/services/${servicesPageSchema.url}/`
                    : `https://www.zerobounce${buildUrlEnding}/services/${servicesPageSchema.url}/`
                }"
              }
            ]
          }
         `}</script>
        )}
        {apiPageSchema && (
          <script data-react-helmet="true" type="application/ld+json" async>{`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "${isEs ? 'Inicio' : 'Home'}",
                "item": "${isEs ? 'https://www.zerobounce.net/es/' : `https://www.zerobounce${buildUrlEnding}`}"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "API",
                "item": "${isEs ? 'https://www.zerobounce.net/es/api/' : `https://www.zerobounce${buildUrlEnding}/api/`}"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "${apiPageSchema.pageName}",
                "item": "${
                  isEs
                    ? `https://www.zerobounce.net/es/api/${apiPageSchema.url}/`
                    : `https://www.zerobounce${buildUrlEnding}/api/${apiPageSchema.url}/`
                }"
              }
            ]
          }
         `}</script>
        )}
      </Helmet>
      <Helmet>{headLinks}</Helmet>
    </React.Fragment>
  );
};

export default Seo;
